import { ArcElement, Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useRef, useState } from 'react';
import { Pie } from 'react-chartjs-2';

import LoadingCircle from '~components/Loading';
import { ICompanyWorkLife } from '~types/company';
import { getPercentage } from '~utils/formatted/string';
import { isEqual } from '~utils/lodash';

import { useChartProps } from './hooks';
import Legends from './Legends';
import * as S from './style';
import TooltipWeekendWork, { TooltipWeekendWorkProps } from './TooltipWeekendWork';

interface Props {
  chartData?: ICompanyWorkLife['weekend'];
  isEmpty?: boolean;
}

ChartJS.register(ChartDataLabels, ArcElement);

export default function PieChart({ chartData, isEmpty }: Props) {
  const chartDataValues = Object.values(chartData ?? {});
  const [tooltipProps, setTooltipProps] = useState<TooltipWeekendWorkProps>({
    $x: 0,
    $y: 0,
    $opacity: 0,
    message: '',
  });
  const chartInstance = useRef(null);
  const { data, options } = useChartProps({
    chartData: chartDataValues,
    externalTooltipHandler: (ctx) => {
      const [pointInfo] = ctx.tooltip.dataPoints;

      const newTooltipProps: TooltipWeekendWorkProps = {
        $x: ctx.tooltip.caretX,
        $y: ctx.tooltip.caretY,
        $opacity: ctx.tooltip.opacity,
        message: `${pointInfo.label}: ${pointInfo.raw.count} (${getPercentage(
          pointInfo.raw.rate,
        )}%)`,
      };

      if (!isEqual(newTooltipProps, tooltipProps)) {
        setTooltipProps(newTooltipProps);
      }
    },
    setTooltipProps,
  });

  const handleMouseEnterLegend = (index: number) => {
    if (chartInstance.current) {
      const activeElement = {
        datasetIndex: 0,
        index,
      };
      const tooltip = chartInstance.current.tooltip;
      chartInstance.current.setActiveElements([activeElement]);
      tooltip.setActiveElements([activeElement], []);
      chartInstance.current.update();
    }
  };

  const handleMouseLeaveLegend = (index: number) => {
    if (chartInstance.current) {
      const tooltip = chartInstance.current.tooltip;
      chartInstance.current.setActiveElements([]);
      tooltip.setActiveElements([], []);
    }
  };

  if (!chartData) {
    return <LoadingCircle />;
  }

  return (
    <S.ChartWrapper>
      {isEmpty ? (
        <S.EmptyChartWrapper>
          <S.DummyPieChart />
          <S.NoData>등록된 리뷰가 없습니다.</S.NoData>
        </S.EmptyChartWrapper>
      ) : (
        <>
          <Legends onMouseEnter={handleMouseEnterLegend} onMouseLeave={handleMouseLeaveLegend} />
          <S.PieWrapper>
            <Pie ref={chartInstance} data={data} options={options} />
            {tooltipProps.$opacity > 0 && <TooltipWeekendWork {...tooltipProps} />}
          </S.PieWrapper>
        </>
      )}
    </S.ChartWrapper>
  );
}
